/**
 * This module manages the attachments model of the API which just manages the CRUD operations
 * for attachment entry metadata.
 *
 * For attachment to DB models and process choreography, see attachments.js
 * For attachments collection CRUD, see attachments.js
 * For uploading (to s3), see upload.js
 *
 * @attachment
 * @author Benjamin Listwon <ben@thebrandingfarm.com>
 * @author Jason Haddix <jason@thebrandingfarm.com>
 */
import debounce from 'lodash/debounce'

import api from '@/api'
import itsApi from '@/api/its'

import {
    VUEX_ATTACHMENTS_INDEX_REQUEST,
    VUEX_ATTACHMENTS_INDEX_SUCCESS,
	VUEX_ATTACHMENTS_INDEX_FAILURE,
	VUEX_ATTACHMENT_SHOW_REQUEST,
    VUEX_ATTACHMENT_SHOW_SUCCESS,
	VUEX_ATTACHMENT_SHOW_FAILURE,
	VUEX_ATTACHMENT_CREATE_REQUEST,
	VUEX_ATTACHMENT_CREATE_SUCCESS,
	VUEX_ATTACHMENT_CREATE_FAILURE,

	// ITS ASSETS
	VUEX_ATTACHMENTS_UPDATE_ITS_PRODUCT_TERM,
	VUEX_ATTACHMENTS_GET_ITS_PRODUCTS,
	VUEX_ATTACHMENTS_GET_ITS_PRODUCTS_SUCCESS,
	VUEX_ATTACHMENTS_GET_ITS_PRODUCTS_FAILURE,
	VUEX_ATTACHMENTS_CLEAR_ITS_PRODUCT_DATA,

	VUEX_ATTACHMENTS_UPDATE_ITS_ASSET_TERM,
	VUEX_ATTACHMENTS_GET_ITS_ASSETS,
	VUEX_ATTACHMENTS_GET_ITS_ASSETS_SUCCESS,
	VUEX_ATTACHMENTS_GET_ITS_ASSETS_FAILURE,
	VUEX_ATTACHMENTS_CLEAR_ITS_ASSET_DATA,

	VUEX_ATTACHMENTS_DELETE_ATTACHMENT,

	VUEX_ATTACHMENT_PERFORM_ATTACH_TO_RESOLUTION
} from '@/store/constants/attachments'
import {
	VUEX_ATTACHMENT_QUEUE_MANAGER_ATTACH_TO_RESOLUTION_SUCCESS,
	VUEX_ATTACHMENT_QUEUE_MANAGER_ASSIGN_COMMENT_ID
} from '@/store/constants/attachment_queue_manager'
import {
	VUEX_JOB_ATTACH_FILE,
	VUEX_JOB_UPDATE_UNPUBLISHED_COMMENT_SET_HAS_ATTACHMENTS,
	VUEX_JOB_COMMENT_ATTACH_FILE
} from '@/store/constants/jobs'
import {
	VUEX_CALENDAR_ATTACH_FILE
} from '@/store/constants/calendarItems'
import {
	VUEX_USER_ATTACH_FILE
} from '@/store/constants/users'
import {
	VUEX_MEMO_ATTACH_FILE
} from '@/store/constants/memos'
import {
	VUEX_DOC_ATTACH_FILE
} from '@/store/constants/docs'

import {
    VUEX_PREMEDIA_ATTACH_FILE
} from '@/store/constants/premedia'




/**
 * Module state
 * @const
 */
const state = {
	// These are for general attachment work
	attachments: [],
	attachment:  {},

	searchProductsData: [],                 // Product data from ITS API
	searchProductsTerm: null,               // Product term entered by user
	searchProductsLoading: false,           // If are currently being looked up

	searchAssetsData: [],                   // Asset data from ITS API
	searchAssetsTerm: null,                 // Asset term entered by user
	searchAssetsLoading: false,             // If are currently being looked up

	// Record an error for later
	lastError: null,
}

/**
 * Module getters
 * @const
 */
const getters = {}

/**
 * Module action definitions
 * @const
 */
const actions = {

	/**
	 * Fetch a list of attachments
	 */
    [VUEX_ATTACHMENTS_INDEX_REQUEST]: async ({ commit }) => {
        commit(VUEX_ATTACHMENTS_INDEX_REQUEST)

        await api.get('/attachments').then(async (response) => {
			await commit(VUEX_ATTACHMENTS_INDEX_SUCCESS, response.data.data)
		}).catch((err) => {
			commit(VUEX_ATTACHMENTS_INDEX_FAILURE, err)
		})
	},

	/**
	 * Fetch a single attachment by ID
	 */
	[VUEX_ATTACHMENT_SHOW_REQUEST]: async ({ commit }, attachmentId) => {
        commit(VUEX_ATTACHMENT_SHOW_REQUEST)

        await api.get(`/attachments/${attachmentId}`).then(async (response) => {
			await commit(VUEX_ATTACHMENT_SHOW_SUCCESS, response.data.data)
		}).catch((err) => {
			commit(VUEX_ATTACHMENT_SHOW_FAILURE, err)
		})
	},

	/**
	 * Create a attachment record
	 */
	[VUEX_ATTACHMENT_CREATE_REQUEST]: async ({ commit }, payload) => {
		commit(VUEX_ATTACHMENT_CREATE_REQUEST)

		await api.post('/attachments', payload).then(async (response) => {
			await commit(VUEX_ATTACHMENT_CREATE_SUCCESS, response.data.data)
		}).catch((err) => {
			commit(VUEX_ATTACHMENT_CREATE_FAILURE, err)
		})
	},


	/**
	 * This is the method that the AQM will call to figure out what other Vuex
	 * actions we need to dispatch based on the 'attachTo' and usage type of
	 * an object that comes in. I will lovingly refer to it as "The Attacher"
	 */
	[VUEX_ATTACHMENT_PERFORM_ATTACH_TO_RESOLUTION]: async ({ commit, dispatch, state, rootState }, payload) => {
        switch (payload.usage_type) {

			case SILA_FILE_USAGE_TYPE_JOB_ATTACHMENT:
			case SILA_FILE_USAGE_TYPE_ITS_ASSET:
			case SILA_FILE_USAGE_TYPE_ITS_PRODUCT:
			case SILA_FILE_USAGE_TYPE_FTP_URL:

				// If we are attaching to a job, it can be any of three types of attachment areas
				switch (payload.attach_to.model) {

					case SILA_FILE_ATTACHMENT_MODEL_JOB:
						// Attach the file to the Job directly
						await dispatch(VUEX_JOB_ATTACH_FILE, payload)
						break // inner switch

					case SILA_FILE_ATTACHMENT_MODEL_JOB_COMMENT:
						// Attach the file to a JobComment
						let commentId = payload.attach_to.model_id

						// If we didn't get a comment id in payload, see if one is open in jobs store
						if (!commentId) {
							let comment = rootState.jobs.jobPendingComment
							if (comment) {
								commentId = comment.id
							}
						}

						// Lastly, if we have an id, go ahead and make the attachments
						if (commentId && rootState.jobs.jobPendingComment) {
							await dispatch(VUEX_JOB_COMMENT_ATTACH_FILE, payload).then(() => {
								// Save the comment id back to the file object so that we can
								// filter uploads in all the file lists by the comment (or other
								// things) they are associated with
								commit(VUEX_ATTACHMENT_QUEUE_MANAGER_ASSIGN_COMMENT_ID, {
									hashId: payload.hashId,
									commentId: commentId
								})

								// Set this in the store so we can keep track when we nav away
								commit(VUEX_JOB_UPDATE_UNPUBLISHED_COMMENT_SET_HAS_ATTACHMENTS, true)
							})
						} else {
							console.error('No comment id available for atatchment')
						}
						break // inner switch

					default:
						break  // inner switch
				}


				break // outer switch

			// TIME OFF REQUEST
			case SILA_FILE_USAGE_TYPE_TIME_OFF_REQUEST_ATTACHMENT:
			case SILA_FILE_USAGE_TYPE_ABSENCE_NOTICE_ATTACHMENT:
				await dispatch(VUEX_CALENDAR_ATTACH_FILE, payload)
				break

			// USER AVATAR
			case SILA_FILE_USAGE_TYPE_USER_AVATAR:
				// Attach the file to the user
				await dispatch(VUEX_USER_ATTACH_FILE, payload)
				break


			// TODO Types
			case SILA_FILE_USAGE_TYPE_MEMO_ATTACHMENT:
				// Attach the file to the Memo
				await dispatch(VUEX_MEMO_ATTACH_FILE, payload)
				break


			case SILA_FILE_USAGE_TYPE_DOCUMENT_ATTACHMENT:
				await dispatch(VUEX_DOC_ATTACH_FILE, payload)
				break


            case SILA_FILE_USAGE_TYPE_JOB_PREMEDIA_ATTACHMENT:
                // Attach the file to the PREMEDIA
                await dispatch(VUEX_PREMEDIA_ATTACH_FILE, payload)
                break

			default:
				break
		}

		dispatch(VUEX_ATTACHMENT_QUEUE_MANAGER_ATTACH_TO_RESOLUTION_SUCCESS, payload)
	},







	// ITS PRODUCTS
	[VUEX_ATTACHMENTS_UPDATE_ITS_PRODUCT_TERM]: async({ commit, dispatch }, payload) => {
		if (!payload) return
		dispatch(VUEX_ATTACHMENTS_GET_ITS_PRODUCTS, payload)
		commit(VUEX_ATTACHMENTS_GET_ITS_PRODUCTS)
	},

	[VUEX_ATTACHMENTS_GET_ITS_PRODUCTS]: debounce(async ({ commit }, payload) => {
		await itsApi.post(`/products/sila`, {term:payload}).then((response) => {
			if (!payload) return
			commit(VUEX_ATTACHMENTS_GET_ITS_PRODUCTS_SUCCESS, response.data)
		}).catch((err) => {
			commit(VUEX_ATTACHMENTS_GET_ITS_PRODUCTS_FAILURE)
		})
	}, 500),


	// ITS ASSETS
	[VUEX_ATTACHMENTS_UPDATE_ITS_ASSET_TERM]: async({ commit, dispatch }, payload) => {
		if (!payload) return
		dispatch(VUEX_ATTACHMENTS_GET_ITS_ASSETS, payload)
		commit(VUEX_ATTACHMENTS_GET_ITS_ASSETS)
	},

	[VUEX_ATTACHMENTS_GET_ITS_ASSETS]: debounce(async ({ commit }, payload) => {
		await itsApi.post(`/assets/files/sila`, {term:payload}).then((response) => {
			if (!payload) return
			commit(VUEX_ATTACHMENTS_GET_ITS_ASSETS_SUCCESS, response.data)
		}).catch((err) => {
			commit(VUEX_ATTACHMENTS_GET_ITS_ASSETS_FAILURE)
		})
	}, 500),

	[VUEX_ATTACHMENTS_DELETE_ATTACHMENT]: async({commit}, payload) => {
	    return await api.delete(`/attachments/`, {data:{...payload}}).then(async (response) => {
			return response
		}).catch((err) => {
			return err
		})
	}
}

/**
 * Module mutation definitions
 * @const
 */
const mutations = {

	/**
	 * Get a single attachment object from the DB
	 */
	[VUEX_ATTACHMENTS_INDEX_REQUEST]: () => {},

	[VUEX_ATTACHMENTS_INDEX_SUCCESS]: (state, payload) => {
		state.attachments = payload
	},

	[VUEX_ATTACHMENTS_INDEX_FAILURE]: (state, error) => {
		state.lastError = error
	},

	/**
	 * Get a single attachment object from the DB
	 */
	[VUEX_ATTACHMENT_SHOW_REQUEST]: () => {},

	[VUEX_ATTACHMENT_SHOW_SUCCESS]: (state, payload) => {
		state.attachment = payload
	},

	[VUEX_ATTACHMENT_SHOW_FAILURE]: (state, error) => {
		state.lastError = error
	},

	/**
	 * The DB object creation for new attachments
	 */
	[VUEX_ATTACHMENT_CREATE_REQUEST]: () => {},

	[VUEX_ATTACHMENT_CREATE_SUCCESS]: (state, payload) => {
		state.attachment = payload
	},

	[VUEX_ATTACHMENT_CREATE_FAILURE]: (state, error) => {
		state.attachment = {}
		state.lastError = error
	},







	/********************** ITS ATTACHMENT API **********************/
	/********************** ITS ATTACHMENT API **********************/

	// ITS PRODUCTS
	[VUEX_ATTACHMENTS_GET_ITS_PRODUCTS]: (state) => {
		state.searchProductsLoading = true
	},

	[VUEX_ATTACHMENTS_GET_ITS_PRODUCTS_SUCCESS]: (state, payload) => {
		state.searchProductsData = payload
		state.searchProductsLoading = false
	},

	[VUEX_ATTACHMENTS_GET_ITS_PRODUCTS_FAILURE]: (state) => {
		state.searchProductsLoading = false
	},

	[VUEX_ATTACHMENTS_CLEAR_ITS_PRODUCT_DATA]: (state) => {
		state.searchProductsData = []
		state.searchProductsTerm = null
		state.searchProductsLoading = false
	},

	/*[VUEX_ATTACHMENTS_ADD_ITS_PRODUCT]: (state, payload) => {
		// push them to current data to display item before save
		// state.premediaRows[state.currentEditingRowIndex].items.push(cloneDeep(payload))
		// push to staging array for item batch upload
		// state.premediaRow_ItemsToCreate.push(cloneDeep(payload))
	},*/



	// ITS ASSETS
	[VUEX_ATTACHMENTS_GET_ITS_ASSETS]: (state) => {
		state.searchAssetsLoading = true
	},

	[VUEX_ATTACHMENTS_GET_ITS_ASSETS_SUCCESS]: (state, payload) => {
		state.searchAssetsData = payload
		state.searchAssetsLoading = false
	},

	[VUEX_ATTACHMENTS_GET_ITS_ASSETS_FAILURE]: (state) => {
		state.searchAssetsLoading = false
	},

	[VUEX_ATTACHMENTS_CLEAR_ITS_ASSET_DATA]: (state) => {
		state.searchAssetsData = []
		state.searchAssetsTerm = null
		state.searchAssetsLoading = false
	},

	/*[VUEX_ATTACHMENTS_ADD_ITS_ASSET]: (state, payload) => {
		// push them to current data to display item before save
		// state.premediaRows[state.currentEditingRowIndex].items.push(cloneDeep(payload))
		// push to staging array for item batch upload
		// state.premediaRow_ItemsToCreate.push(cloneDeep(payload))
	},*/

}

/**
 * The Files module of our Vuex store.
 * @module store/modules/attachments
 */
export default {
  state,
  getters,
  actions,
  mutations,
}




// KEEP for fallback for a bit
// if (!commentId) {
// 	let comment = rootState.jobs.jobPendingComment
// 	if (!comment) {
// 		await dispatch(VUEX_JOB_ADD_UNPUBLISHED_COMMENT, {
// 			job_id: jobId,
// 			is_unpublished: true,
// 		}).then(() => {
// 			comment = rootState.jobs.jobPendingComment
// 			commentId = comment.id
// 			// payload.attach_to[commentIndex].model_id = comment.id
// 		})
// 	}
// }

// // Once we have a comment, attach some stuff yo!
// await dispatch(VUEX_JOB_COMMENT_ATTACH_FILE, {
// 	job_id: jobId,
// 	comment_id: commentId,
// 	file_id: payload.id
// })
